import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FileText, Upload, Download, CheckCircle, Clock, AlertTriangle,
  ExternalLink, TrendingUp, Activity, Archive, Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CATEGORY_META = {
  '20_day_progress': { label: '20-Day Progress', color: 'bg-emerald-800/30 text-emerald-300', icon: TrendingUp },
  decode: { label: 'DECODE Report', color: 'bg-rose-800/30 text-rose-300', icon: Activity },
  decode_pdf: { label: 'DECODE (PDF)', color: 'bg-rose-800/30 text-rose-300', icon: FileText },
  predecode: { label: 'PreDECODE', color: 'bg-amber-800/30 text-amber-300', icon: FileText },
  lab_recommendation: { label: 'Lab Recommendation', color: 'bg-cyan-800/30 text-cyan-300', icon: FileText },
  manual_upload: { label: 'Premium Report', color: 'bg-violet-800/30 text-violet-300', icon: FileText },
  premium_report: { label: 'Premium Report', color: 'bg-violet-800/30 text-violet-300', icon: FileText },
};
const STATUS_COLORS = {
  draft: 'bg-amber-800/30 text-amber-300', published: 'bg-emerald-800/30 text-emerald-300',
  uploaded: 'bg-blue-800/30 text-blue-300', archived: 'bg-white/5 text-white/30',
};

export default function ReportsHub() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSub, setSelectedSub] = useState('');
  const [reports, setReports] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [statusView, setStatusView] = useState('active'); // 'active' | 'archived' | 'all'

  useEffect(() => {
    axios.get(`${API}/users?role=subscriber`).then(r => setSubscribers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedSub) { loadReports(); loadEligibility(); }
  }, [selectedSub]);

  const loadReports = async () => {
    try { const r = await axios.get(`${API}/reports-hub/${selectedSub}`); setReports(r.data.reports || []); } catch {}
  };
  const loadEligibility = async () => {
    try { const r = await axios.get(`${API}/reports-hub/${selectedSub}/20day-eligibility`); setEligibility(r.data); } catch {}
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file || !selectedSub) return;
    const title = prompt('Report title:', file.name);
    if (!title) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('title', title); fd.append('report_type', 'premium_report');
    try { await axios.post(`${API}/reports-hub/${selectedSub}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Report uploaded'); loadReports(); } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleStatusChange = async (reportId, status) => {
    try { await axios.patch(`${API}/reports-hub/${reportId}/status`, { status }); toast.success(`Report ${status}`); loadReports(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleArchive = async (reportId) => {
    if (!confirm('Archive this report? Subscribers will no longer see it.')) return;
    handleStatusChange(reportId, 'archived');
  };

  const handleRestore = async (reportId) => {
    handleStatusChange(reportId, 'draft');
  };

  const visibleReports = reports.filter(r => {
    if (statusView === 'all') return true;
    if (statusView === 'archived') return r.status === 'archived';
    return r.status !== 'archived';
  });
  const archivedCount = reports.filter(r => r.status === 'archived').length;
  const activeCount = reports.length - archivedCount;

  const handle20DayGenerate = async () => {
    if (!selectedSub) return;
    try {
      await axios.post(`${API}/reports/generate-20day/${selectedSub}`);
      toast.success('20-day report generated as draft');
      loadReports(); loadEligibility();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate'); }
  };

  return (
    <div className="min-h-screen bg-[#080C14] text-white p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-[#C89B7B]">Reports Hub</h1>
            <p className="text-xs text-white/30 mt-0.5">All reports — PreDECODE, DECODE, Progress, Premium uploads</p>
          </div>
        </div>

        {/* Subscriber selector + actions */}
        <div className="flex gap-3 items-end flex-wrap">
          <div className="w-72">
            <label className="text-[9px] text-white/30 block mb-1">Subscriber</label>
            <Select value={selectedSub} onValueChange={setSelectedSub}>
              <SelectTrigger className="bg-white/[0.03] border-white/[0.08] text-white text-xs"><SelectValue placeholder="Select subscriber" /></SelectTrigger>
              <SelectContent className="bg-[#1a1f2e] border-white/10 max-h-60">
                {subscribers.map(s => <SelectItem key={s.id} value={s.id} className="text-white text-xs">{s.name} ({s.email})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {selectedSub && (
            <>
              <label className="h-9 px-4 text-xs bg-violet-700/30 hover:bg-violet-700/50 text-violet-300 rounded-md flex items-center gap-2 cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploading...' : 'Upload Report PDF'}
                <input type="file" accept=".pdf" className="hidden" data-testid="report-upload" onChange={handleUpload} disabled={uploading} />
              </label>
            </>
          )}
        </div>

        {selectedSub && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Reports list */}
            <div className="lg:col-span-2 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-sm text-white/50">All Reports ({reports.length})</h2>
                <div className="flex items-center gap-1.5" data-testid="report-status-filter">
                  {[
                    { value: 'active', label: `Active (${activeCount})` },
                    { value: 'archived', label: `Archived (${archivedCount})` },
                    { value: 'all', label: `All (${reports.length})` },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setStatusView(opt.value)}
                      data-testid={`report-status-${opt.value}`}
                      className={`px-2.5 py-1 rounded-full text-[10px] border transition-colors ${
                        statusView === opt.value
                          ? 'bg-violet-700/30 text-violet-200 border-violet-500/30'
                          : 'bg-white/[0.02] text-white/40 border-white/10 hover:text-white/60'
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
              {visibleReports.length === 0 ? (
                <Card className="bg-white/[0.02] border-white/[0.06]"><CardContent className="p-8 text-center text-white/30 text-sm" data-testid="reports-empty">
                  {statusView === 'archived'
                    ? 'No archived reports.'
                    : statusView === 'active'
                      ? 'No active reports for this subscriber.'
                      : 'No reports yet for this subscriber.'}
                </CardContent></Card>
              ) : visibleReports.map(r => {
                const meta = CATEGORY_META[r.report_type] || CATEGORY_META[r.category] || { label: r.report_type, color: 'bg-white/5 text-white/30', icon: FileText };
                const Icon = meta.icon;
                const isArchived = r.status === 'archived';
                return (
                  <Card key={r.id} className={`bg-white/[0.02] border-white/[0.06] ${isArchived ? 'opacity-60' : ''}`} data-testid={`report-card-${r.id}`}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Icon className="w-4 h-4 text-white/20 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-white/80 truncate">{r.title || r.filename || r.report_type}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge className={`text-[7px] ${meta.color}`}>{meta.label}</Badge>
                            <Badge className={`text-[7px] ${STATUS_COLORS[r.status] || 'bg-white/5 text-white/30'}`} data-testid={`report-status-badge-${r.id}`}>{r.status}</Badge>
                            {r.created_at && <span className="text-[8px] text-white/20">{new Date(r.created_at).toLocaleDateString()}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {r.file_url && (
                          <a href={r.file_url} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[9px] text-white/30"><Download className="w-3 h-3" /></Button>
                          </a>
                        )}
                        {r.status === 'draft' && r.source === 'generated' && (
                          <Button size="sm" className="h-7 px-2.5 text-[9px] bg-emerald-700/30 text-emerald-300" onClick={() => handleStatusChange(r.id, 'published')} data-testid={`report-publish-${r.id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Publish
                          </Button>
                        )}
                        {isAdmin && !isArchived && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[9px] text-white/30 hover:text-amber-300" onClick={() => handleArchive(r.id)} data-testid={`report-archive-${r.id}`}>
                            <Archive className="w-3 h-3 mr-1" /> Archive
                          </Button>
                        )}
                        {isAdmin && isArchived && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[9px] text-white/40 hover:text-emerald-300" onClick={() => handleRestore(r.id)} data-testid={`report-restore-${r.id}`}>
                            <Undo2 className="w-3 h-3 mr-1" /> Restore
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Right sidebar — 20-day eligibility + quick actions */}
            <div className="space-y-4">
              {/* 20-Day Report Card */}
              <Card className="bg-white/[0.02] border-white/[0.06]">
                <CardContent className="p-4 space-y-3">
                  <h3 className="text-xs text-white/50 uppercase tracking-wider">20-Day Progress Report</h3>
                  {eligibility ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {eligibility.eligible ? (
                          <Badge className="bg-emerald-800/30 text-emerald-300 text-[9px]"><CheckCircle className="w-3 h-3 mr-1" /> Eligible</Badge>
                        ) : (
                          <Badge className="bg-amber-800/30 text-amber-300 text-[9px]"><AlertTriangle className="w-3 h-3 mr-1" /> Not Yet Eligible</Badge>
                        )}
                      </div>
                      {/* Transparent counters */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-white/40">Execution Days</span>
                          <span className={eligibility.execution_days >= eligibility.execution_required ? 'text-emerald-400' : 'text-amber-400'}>
                            {eligibility.execution_days} / {eligibility.execution_required}
                          </span>
                        </div>
                        <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${eligibility.execution_days >= eligibility.execution_required ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${Math.min(100, (eligibility.execution_days / eligibility.execution_required) * 100)}%` }} />
                        </div>
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-white/40">MindFlo Check-ins</span>
                          <span className={eligibility.mindflo_days >= eligibility.mindflo_required ? 'text-emerald-400' : 'text-amber-400'}>
                            {eligibility.mindflo_days} / {eligibility.mindflo_required}
                          </span>
                        </div>
                        <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${eligibility.mindflo_days >= eligibility.mindflo_required ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${Math.min(100, (eligibility.mindflo_days / eligibility.mindflo_required) * 100)}%` }} />
                        </div>
                      </div>
                      {!eligibility.eligible && (
                        <p className="text-[9px] text-white/25 mt-1">{eligibility.reason}</p>
                      )}
                      {eligibility.existing_report_count > 0 && (
                        <p className="text-[9px] text-white/25">{eligibility.existing_report_count} existing report{eligibility.existing_report_count > 1 ? 's' : ''}</p>
                      )}
                      <Button data-testid="generate-20day-btn" size="sm" disabled={!eligibility.eligible}
                        className={`w-full h-8 text-[10px] ${eligibility.eligible ? 'bg-[#4E7A6B] hover:bg-[#3D695B] text-white' : 'bg-white/5 text-white/20 cursor-not-allowed'}`}
                        onClick={handle20DayGenerate}>
                        <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Generate 20-Day Report
                      </Button>
                    </div>
                  ) : (
                    <p className="text-[10px] text-white/20">Loading eligibility...</p>
                  )}
                </CardContent>
              </Card>

              {/* Quick info */}
              <Card className="bg-white/[0.02] border-white/[0.06]">
                <CardContent className="p-4 space-y-2">
                  <h3 className="text-xs text-white/50 uppercase tracking-wider">Report Types</h3>
                  <div className="space-y-1">
                    {Object.entries(CATEGORY_META).filter(([k]) => !['decode_pdf', 'premium_report'].includes(k)).map(([key, { label, color }]) => (
                      <div key={key} className="flex items-center gap-2">
                        <Badge className={`text-[7px] ${color}`}>{label}</Badge>
                        <span className="text-[8px] text-white/15">{reports.filter(r => r.report_type === key || r.category === key).length}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
